import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { WheelGameModel } from '../../model/wheel/WheelGameModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface WheelHudCallbacks {
  onSpin: () => void;
}

/**
 * The HUD around the wheel: balance counter (top centre), the cyan
 * SPIN-THE-WHEEL button (below the wheel), and a status label below the
 * button.
 *
 * <p>Subscribes directly to {@link WheelGameModel} so balance updates are
 * automatic. The host scene drives the button via {@link setSpinning} and
 * shows ad-hoc messages via {@link setStatusText}.
 */
export class WheelHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly spinButton: PrimaryButton;
  private readonly statusLabel: Text;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: WheelGameModel,
    private readonly callbacks: WheelHudCallbacks,
  ) {
    super();
    this.balance = new BalanceDisplay(this.model.balance);
    this.spinButton = new PrimaryButton({
      label: 'SPIN THE WHEEL',
      width: 220,
      fillActive: 0x42e2f5,
      fillHover: 0x2a4d75,
      textColor: 0x1a0830,
      textSize: 18,
      letterSpacing: 1.5,
      onTap: () => this.callbacks.onSpin(),
    });
    this.statusLabel = this.buildStatusLabel();
    this.addChild(this.balance, this.spinButton, this.statusLabel);

    this.subscriptions.push(
      this.model.events.on('balance-changed', () => {
        this.balance.setAmount(this.model.balance);
      }),
      this.model.events.on('not-enough-coins', () => {
        this.statusLabel.text = `not enough coins for the bet — refresh to reset`;
      }),
    );
  }

  layout(ctx: SceneLayoutContext): void {
    this.balance.x = ctx.centerX;
    this.balance.y = Math.max(16, ctx.machineTop - 96);

    this.spinButton.x = ctx.centerX;
    this.spinButton.y = ctx.spinButtonY;

    this.statusLabel.x = ctx.centerX;
    this.statusLabel.y = ctx.spinButtonY + 56;
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  setSpinning(spinning: boolean): void {
    this.spinButton.setLabel(spinning ? '…' : 'SPIN THE WHEEL');
    this.spinButton.setSuppressHover(spinning);
  }

  setStatusText(text: string): void {
    this.statusLabel.text = text;
  }

  /** Default hint shown after init / after a spin completes. */
  showDefaultHint(betPerSpin: number): void {
    this.statusLabel.text = `bet ${betPerSpin} coins · tap or press space`;
  }

  private buildStatusLabel(): Text {
    const t = new Text({
      text: `bet ${this.model.betPerSpin} coins · tap or press space`,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'center',
      },
    });
    t.anchor.set(0.5);
    t.alpha = 0.7;
    return t;
  }
}
